import { IconButton } from 'omakase';
import { IconFacebook, IconInstagram, IconShare as IconShareLegacy, IconTwitter } from 'ui/icons';
import { IconLogoFacebook, IconLogoInstagram, IconLogoTwitter, IconShare } from '@tock/icons';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import { MobileShareLink } from 'components/MobileShareLink';
import { Theme, css } from '@emotion/react';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import Anchor from 'components/Anchor';
import getSocialUrl from 'utils/getSocialUrl';

interface SocialLinksProps {
  facebookUrl?: string;
  instagramId?: string;
  twitterHandle?: string;
  shareLink?: string;
  name?: string;
}

const iconCss = (theme: Theme) => css`
  color: ${theme.colors.fgPrimaryBase};
`;

const SocialLinks: React.FunctionComponent<SocialLinksProps> = ({
  facebookUrl,
  instagramId,
  twitterHandle,
  shareLink,
  name,
}) => {
  const fullFacebookUrl = getSocialUrl('https://www.facebook.com/', facebookUrl);
  const fullInstagramUrl = getSocialUrl('https://www.instagram.com/', instagramId);
  const fullTwitterUrl = getSocialUrl('https://www.twitter.com/', twitterHandle);
  const isNativeShareEnabled = useFeatureFlag('CONSUMER_MOBILE_NATIVE_SHARE');

  // don't render at all if there is nothing
  if (!fullFacebookUrl && !fullInstagramUrl && !fullTwitterUrl && !shareLink) {
    return null;
  }

  return (
    <div className="SocialLinks">
      {fullFacebookUrl ? (
        <IconButton
          data-testid="facebook-link"
          css={iconCss}
          component={Anchor}
          href={fullFacebookUrl}
          aria-label={`View ${name} on Facebook`}
          size="medium"
          variant="ghost"
        >
          <IconMigrationUtil legacyIcon={<IconFacebook />} newIcon={<IconLogoFacebook />} />
        </IconButton>
      ) : null}

      {fullInstagramUrl ? (
        <IconButton
          data-testid="instagram-link"
          css={iconCss}
          component={Anchor}
          href={fullInstagramUrl}
          aria-label={`View ${name} on Instagram`}
          size="medium"
          variant="ghost"
        >
          <IconMigrationUtil legacyIcon={<IconInstagram />} newIcon={<IconLogoInstagram />} />
        </IconButton>
      ) : null}

      {fullTwitterUrl ? (
        <IconButton
          data-testid="twitter-link"
          css={iconCss}
          component={Anchor}
          href={fullTwitterUrl}
          aria-label={`View ${name} on Twitter`}
          size="medium"
          variant="ghost"
        >
          <IconMigrationUtil legacyIcon={<IconTwitter />} newIcon={<IconLogoTwitter />} />
        </IconButton>
      ) : null}

      {shareLink && isNativeShareEnabled ? (
        <MobileShareLink
          title={`Share ${name}`}
          text={`Check out ${name} on Tock!`}
          url={shareLink}
        >
          <IconButton css={iconCss} aria-label={`Share ${name}`} size="medium" variant="ghost">
            <IconMigrationUtil legacyIcon={<IconShareLegacy />} newIcon={<IconShare />} />
          </IconButton>
        </MobileShareLink>
      ) : null}
    </div>
  );
};

export default SocialLinks;
